import React, { useMemo, useState, useEffect } from 'react';
import { SpecialEvent, DayOfWeek, SearchParams } from '../types';
import SaveNotificationModal from './SaveNotificationModal';
import { SpecialCard } from './SpecialCard';
import { geocodeLocation, geocodeVenue, getBrowserLocation, hasUsableCoords, haversineMiles, normalizeLocationInput, type Coordinates } from '../services/locationSearch';
import { fetchVenueCoordinates } from '../services/venueCoordinates';

interface ResultsPageProps {
  events: SpecialEvent[];
  searchParams: SearchParams;
  onBack: () => void;
  onVenueSelect: (venue: SpecialEvent, currentParams: SearchParams) => void;
  onPreviewSignup: (venueName: string) => void;
}

const VIBES = [
  'All Vibes',
  'Craft Beer',
  'Cocktails',
  'Trivia',
  'Live Music',
  'Cheap Eats',
  'Patio',
  'Rooftop',
  'Speakeasy',
  'Dog Friendly',
];

const RADIUS_OPTIONS = [5, 10, 25] as const;

const DAY_NAMES_FROM_GET_DAY: DayOfWeek[] = [
  DayOfWeek.SUNDAY,
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
];

function formatLocalDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseLocalDateInput(dateInput: string): Date | null {
  const match = dateInput.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  const parsed = new Date(year, monthIndex, day);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function searchableLocationText(event: SpecialEvent) {
  return [event.venueName, event.address, event.streetAddress, event.city, event.state, event.zipCode]
    .map((value) => String(value || '').toLowerCase())
    .join(' ');
}

function locationSearchTokens(params: SearchParams) {
  const raw = `${params.location || ''} ${params.locationLabel || ''}`.toLowerCase();
  return raw
    .split(/[^a-z0-9]+/)
    .map((token) => token.trim())
    .filter((token) => token.length >= 3)
    .filter((token) => !['current', 'location', 'nearby', 'united', 'states', 'usa'].includes(token));
}

function matchesTypedLocationText(event: SpecialEvent, params: SearchParams) {
  if (!params.location || params.location.toLowerCase() === 'current location') return false;

  const searchable = searchableLocationText(event);
  const tokens = locationSearchTokens(params);

  if (tokens.length === 0) return false;
  return tokens.some((token) => searchable.includes(token));
}

const ResultsPage: React.FC<ResultsPageProps> = ({ events, searchParams: initialParams, onBack, onVenueSelect, onPreviewSignup }) => {
  const [params, setParams] = useState<SearchParams>({ ...initialParams, radiusMiles: initialParams.radiusMiles || 10 });
  const [favorites, setFavorites] = useState<string[]>([]);
  const [showNotifyModal, setShowNotifyModal] = useState<{ venueName: string; venueId?: string; city?: string } | null>(null);
  const [filter, setFilter] = useState<'All' | 'Beer' | 'Wine' | 'Cocktail' | 'Food' | 'Mixed' | 'Favorites'>(
    ((initialParams.category === 'Saved' ? 'Favorites' : initialParams.category) as any) || 'All'
  );
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [locationDraft, setLocationDraft] = useState(initialParams.location || '');
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [distanceStatus, setDistanceStatus] = useState<'idle' | 'loading' | 'ready'>('idle');
  const [distanceByEventId, setDistanceByEventId] = useState<Record<string, number | null>>({});
  const [coordsByVenueId, setCoordsByVenueId] = useState<Map<string, Coordinates>>(new Map());
  const [locationMessage, setLocationMessage] = useState<string | null>(initialParams.locationError || null);

  const isAnyDate = !params.date;
  const isAnyTime = !params.time;
  const radiusMiles = Number(params.radiusMiles || 10);

  useEffect(() => {
    const saved = localStorage.getItem('hoppy_favorites');
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);
      setFavorites(Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : []);
    } catch {
      localStorage.removeItem('hoppy_favorites');
      setFavorites([]);
    }
  }, []);

  useEffect(() => {
    setLocationDraft(params.location || '');
  }, [params.location]);

  useEffect(() => {
    let cancelled = false;
    const venueIds = events.map((event) => String(event.venueId || '').trim()).filter(Boolean);

    fetchVenueCoordinates(venueIds).then((coords) => {
      if (!cancelled) setCoordsByVenueId(coords);
    });

    return () => {
      cancelled = true;
    };
  }, [events]);

  useEffect(() => {
    let cancelled = false;

    const calculateDistances = async () => {
      if (!params.coords) {
        setDistanceByEventId({});
        setDistanceStatus('idle');
        return;
      }

      setDistanceStatus('loading');
      const entries: Array<readonly [string, number | null]> = [];

      for (const event of events) {
        if (cancelled) return;

        const savedCoords = event.venueId ? coordsByVenueId.get(event.venueId) : null;
        const directCoords = hasUsableCoords({ lat: event.lat, lng: event.lng })
          ? { lat: event.lat as number, lng: event.lng as number }
          : savedCoords || null;

        const venueCoords = directCoords || await geocodeVenue(event);
        entries.push([
          event.id,
          venueCoords ? haversineMiles(params.coords, venueCoords) : null,
        ] as const);
      }

      if (cancelled) return;

      setDistanceByEventId(Object.fromEntries(entries));
      setDistanceStatus('ready');
    };

    calculateDistances();

    return () => {
      cancelled = true;
    };
  }, [events, params.coords, coordsByVenueId]);

  const updateHistoryState = (nextParams: SearchParams, nextFilter = filter) => {
    const currentState = window.history.state;
    if (currentState && currentState.view === 'results') {
      window.history.replaceState({ ...currentState, searchParams: { ...nextParams, category: nextFilter } }, '', '');
    }
  };

  const applyGeocodedLocation = (locationText: string, lat: number, lng: number, label?: string) => {
    const next = {
      ...params,
      location: locationText,
      locationLabel: label || locationText,
      selectedCities: [],
      coords: { lat, lng },
      locationError: null,
    };

    setParams(next);
    setLocationDraft(locationText);
    updateHistoryState(next);
  };

  const handleLocationSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanedLocation = normalizeLocationInput(locationDraft);

    if (!cleanedLocation) {
      const next = { ...params, location: '', locationLabel: '', coords: null, locationError: null };
      setParams(next);
      setLocationDraft('');
      setLocationMessage(null);
      updateHistoryState(next);
      return;
    }

    setIsSearchingLocation(true);
    setLocationMessage(null);

    const geocoded = await geocodeLocation(cleanedLocation);
    setIsSearchingLocation(false);

    if (!geocoded) {
      setLocationMessage('We could not find that location. Try a ZIP code or add the state, like Plymouth, MI.');
      return;
    }

    applyGeocodedLocation(cleanedLocation, geocoded.lat, geocoded.lng, geocoded.label);
  };

  const handleUseCurrentLocation = async () => {
    try {
      setIsLocating(true);
      setLocationMessage(null);
      const current = await getBrowserLocation();
      applyGeocodedLocation('Current location', current.lat, current.lng, current.label);
    } catch (err: any) {
      setLocationMessage(err?.message || 'We could not access your current location. You can still search by ZIP or city.');
    } finally {
      setIsLocating(false);
    }
  };

  const setRadius = (miles: 5 | 10 | 25) => {
    const next = { ...params, radiusMiles: miles };
    setParams(next);
    updateHistoryState(next);
  };

  const handleCopyAddress = (e: React.MouseEvent, address: string, id: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(address);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleAnyDate = () => {
    const next = { ...params, date: isAnyDate ? formatLocalDateForInput(new Date()) : '' };
    setParams(next);
    updateHistoryState(next);
  };

  const toggleAnyTime = () => {
    const next = { ...params, time: isAnyTime ? '16:00' : '' };
    setParams(next);
    updateHistoryState(next);
  };

  const setVibe = (vibe: string) => {
    const next = { ...params, vibe: vibe === 'All Vibes' ? undefined : vibe };
    setParams(next);
    updateHistoryState(next);
  };

  const filteredEvents = useMemo(() => {
    const parsedSearchDate = params.date ? parseLocalDateInput(params.date) : null;
    const searchDay = parsedSearchDate ? DAY_NAMES_FROM_GET_DAY[parsedSearchDate.getDay()] : null;
    const now = new Date();
    const norm = (s?: unknown) => String(s ?? '').toLowerCase().trim();

    const baseEvents = Array.isArray(events) ? events.filter((event): event is SpecialEvent => !!event && typeof event === 'object') : [];

    const sorted = [...baseEvents].sort((a, b) => {
      const verifiedDiff = Number(Boolean(b.isVerified ?? b.is_verified)) - Number(Boolean(a.isVerified ?? a.is_verified));
      if (verifiedDiff !== 0) return verifiedDiff;

      const statusScore = (s: string | undefined) => (s === 'live' ? 1 : 0);
      const statusDiff = statusScore(b.listing_status) - statusScore(a.listing_status);
      if (statusDiff !== 0) return statusDiff;

      const aDistance = distanceByEventId[a.id];
      const bDistance = distanceByEventId[b.id];
      if (typeof aDistance === 'number' && typeof bDistance === 'number') return aDistance - bDistance;
      if (typeof aDistance === 'number') return -1;
      if (typeof bDistance === 'number') return 1;

      const planScore = (p: string | undefined) => (p === 'partner' ? 3 : 1);
      return planScore(b.plan) - planScore(a.plan);
    });

    return sorted.filter((event) => {
      const deals = Array.isArray(event.deals) ? event.deals : [];
      const tags = Array.isArray(event.tags) ? event.tags : [];

      if (event.endDate) {
        const endDate = new Date(event.endDate);
        if (now > endDate) return false;
      }

      if (filter === 'Favorites' && !favorites.includes(event.id)) return false;
      if (searchDay && !event.days.includes(searchDay)) return false;

      if (params.time) {
        const [sh, sm] = event.startTime.split(':').map(Number);
        const [eh, em] = event.endTime.split(':').map(Number);
        const [th, tm] = params.time.split(':').map(Number);
        const start = sh * 60 + sm;
        const end = eh * 60 + em;
        const target = th * 60 + tm;

        if (end < start) {
          if (!(target >= start || target <= end)) return false;
        } else if (target < start || target > end) {
          return false;
        }
      }

      if (params.coords) {
        if (distanceStatus !== 'ready') return false;
        const miles = distanceByEventId[event.id];
        if (typeof miles === 'number') return miles <= radiusMiles;
        if (matchesTypedLocationText(event, params)) return true;
        return false;
      }

      if (!params.coords && params.location) {
        if (!matchesTypedLocationText(event, params)) return false;
      }

      if (params.vibe) {
        const searchWords = norm(params.vibe).split(/\s+/).filter(Boolean);
        const matchesVibe = tags.some((tag) => {
          const lowerTag = norm(tag);
          return searchWords.some((word) => lowerTag.includes(word));
        });
        if (!matchesVibe) return false;
      }

      if (filter === 'Mixed') {
        const hasFood = deals.some((deal) => deal.category === 'Food');
        const hasDrink = deals.some((deal) => ['Beer', 'Wine', 'Cocktail'].includes(deal.category));
        return hasFood && hasDrink;
      }

      if (filter !== 'All' && filter !== 'Favorites' && filter !== 'Mixed') {
        return deals.some((deal) => (deal.category as string) === (filter as string));
      }

      return true;
    });
  }, [events, params, filter, favorites, distanceByEventId, distanceStatus, radiusMiles]);

  const categories = ['All', 'Favorites', 'Beer', 'Wine', 'Cocktail', 'Food', 'Mixed'];

  const toggleFavorite = (e: React.MouseEvent, eventId: string, venueName: string, venueId?: string) => {
    e.stopPropagation();
    let newFavorites: string[];
    const isAdding = !favorites.includes(eventId);
    if (isAdding) {
      newFavorites = [...favorites, eventId];
      setShowNotifyModal({ venueName, venueId: venueId || eventId });
    } else {
      newFavorites = favorites.filter((id) => id !== eventId);
    }
    setFavorites(newFavorites);
    localStorage.setItem('hoppy_favorites', JSON.stringify(newFavorites));
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  const locationSummary = params.coords
    ? `${radiusMiles} miles from ${params.location || 'your search'}`
    : params.location
      ? `Matching ${params.location}`
      : 'All active locations';

  const isLoadingDistances = Boolean(params.coords && distanceStatus !== 'ready');

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-24 md:py-40 bg-[#FFFDF5]">
      <div className="mb-12 md:mb-16 relative z-[90] animate-in fade-in slide-in-from-top-4 duration-500">
        <form onSubmit={handleLocationSearch} className="bg-black/95 backdrop-blur-3xl rounded-[3rem] p-3 md:p-4 shadow-2xl border border-white/20 flex flex-col gap-3 relative overflow-visible">
          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_0.8fr_0.8fr_auto] gap-3">
            <div className="relative min-w-0">
              <div className="absolute left-6 top-1/2 -translate-y-1/2 text-amber-500 z-[110]">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                </svg>
              </div>
              <input
                type="text"
                value={locationDraft}
                onChange={(e) => {
                  setLocationDraft(e.target.value);
                  if (locationMessage) setLocationMessage(null);
                }}
                placeholder="ZIP, city, or city/state"
                className="w-full h-14 md:h-16 bg-white/5 border-2 border-transparent focus:border-amber-500/30 focus:bg-white/10 rounded-[2rem] pl-16 pr-8 text-left font-black text-base md:text-lg outline-none transition-all text-amber-500 placeholder-white/25"
              />
            </div>

            <div className={`relative bg-white/5 rounded-[2rem] h-14 md:h-16 flex items-center transition-all min-w-0 border-2 ${isAnyDate ? 'border-amber-500/40 bg-amber-500/5' : 'border-transparent'}`}>
              {isAnyDate ? (
                <div className="flex-1 flex items-center pl-4 text-amber-500 font-black text-xs uppercase tracking-[0.2em] whitespace-nowrap overflow-hidden pr-12">Any Date</div>
              ) : (
                <input
                  type="date"
                  value={params.date}
                  onChange={(e) => {
                    const next = { ...params, date: e.target.value };
                    setParams(next);
                    updateHistoryState(next);
                  }}
                  className="flex-1 bg-transparent text-amber-500 font-black pl-4 pr-12 outline-none text-[13px] md:text-sm w-full h-full [color-scheme:dark]"
                />
              )}
              <button
                type="button"
                onClick={toggleAnyDate}
                className={`absolute right-2 w-10 h-8 rounded-xl text-[8px] font-black transition-all z-10 ${isAnyDate ? 'bg-amber-500 text-black' : 'bg-white/10 text-white/40'}`}
              >
                {isAnyDate ? 'X' : 'ANY'}
              </button>
            </div>

            <div className={`relative bg-white/5 rounded-[2rem] h-14 md:h-16 flex items-center transition-all min-w-0 border-2 ${isAnyTime ? 'border-amber-500/40 bg-amber-500/5' : 'border-transparent'}`}>
              {isAnyTime ? (
                <div className="flex-1 flex items-center pl-4 text-amber-500 font-black text-xs uppercase tracking-[0.2em] whitespace-nowrap overflow-hidden pr-12">Anytime</div>
              ) : (
                <input
                  type="time"
                  value={params.time}
                  onChange={(e) => {
                    const next = { ...params, time: e.target.value };
                    setParams(next);
                    updateHistoryState(next);
                  }}
                  className="flex-1 bg-transparent text-amber-500 font-black pl-4 pr-12 outline-none text-[13px] md:text-sm w-full h-full [color-scheme:dark]"
                />
              )}
              <button
                type="button"
                onClick={toggleAnyTime}
                className={`absolute right-2 w-10 h-8 rounded-xl text-[8px] font-black transition-all z-10 ${isAnyTime ? 'bg-amber-500 text-black' : 'bg-white/10 text-white/40'}`}
              >
                {isAnyTime ? 'X' : 'ANY'}
              </button>
            </div>

            <button
              type="submit"
              disabled={isSearchingLocation}
              className="h-14 md:h-16 px-8 bg-amber-500 text-black rounded-[2rem] font-black text-[10px] uppercase tracking-widest hover:bg-amber-400 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSearchingLocation ? 'Searching...' : 'Update'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
            <div className="grid grid-cols-3 gap-2 bg-white/5 rounded-2xl p-2 border border-white/10">
              {RADIUS_OPTIONS.map((miles) => (
                <button
                  key={miles}
                  type="button"
                  onClick={() => setRadius(miles)}
                  className={`h-11 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all ${radiusMiles === miles ? 'bg-amber-500 text-black shadow-xl' : 'text-white/55 hover:text-white hover:bg-white/10'}`}
                >
                  {miles} miles
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={handleUseCurrentLocation}
              disabled={isLocating}
              className="h-14 md:h-auto px-6 rounded-2xl bg-white/10 text-white font-black text-[10px] uppercase tracking-widest hover:bg-white hover:text-black transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLocating ? 'Locating...' : 'Use My Location'}
            </button>
          </div>
        </form>

        {locationMessage && (
          <div className="mt-4 px-5 py-3 rounded-2xl bg-red-50 border border-red-100 text-red-600 text-sm font-bold text-left">
            {locationMessage}
          </div>
        )}
      </div>

      <div className="mb-12 relative z-10">
        <div className="flex items-center justify-between mb-4 px-2">
          <h3 className="text-sm md:text-base font-black italic uppercase tracking-[0.3em] text-amber-600">Select Your Vibe</h3>
          {params.vibe && (
            <button
              type="button"
              onClick={() => setVibe('All Vibes')}
              className="text-[9px] font-black text-gray-400 hover:text-black uppercase tracking-widest underline underline-offset-4 decoration-2 decoration-gray-100"
            >
              Reset All Vibes
            </button>
          )}
        </div>

        <div className="w-full bg-white rounded-[2.5rem] border border-black/[0.05] shadow-sm py-2 px-2 overflow-hidden">
          <div className="flex gap-3 overflow-x-auto no-scrollbar items-center justify-start px-2 md:px-4 py-1">
            {VIBES.map((vibe) => {
              const isActive = (vibe === 'All Vibes' && !params.vibe) || params.vibe === vibe;
              return (
                <button
                  key={vibe}
                  type="button"
                  onClick={() => setVibe(vibe)}
                  className={`shrink-0 px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 border-2 ${isActive ? 'bg-amber-500 text-black border-amber-500 shadow-xl scale-105' : 'bg-gray-50 text-gray-400 border-transparent hover:border-black/10 hover:text-black hover:bg-white shadow-sm'}`}
                >
                  {vibe}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-8 mb-16 px-2 relative z-0">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl text-left">
            <button
              type="button"
              onClick={onBack}
              className="mb-6 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-black"
            >
              Back
            </button>
            <h2 className="text-5xl md:text-7xl font-black serif tracking-tight leading-[0.9]">Explore <span className="italic text-amber-600">Specials.</span></h2>
            <p className="mt-4 text-gray-500 font-bold text-sm md:text-base uppercase tracking-widest">
              {isLoadingDistances ? 'Checking nearby venues...' : `${filteredEvents.length} result${filteredEvents.length === 1 ? '' : 's'} - ${locationSummary}`}
            </p>
          </div>

          <div className="w-full lg:w-auto lg:max-w-3xl">
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 whitespace-nowrap">
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => {
                    setFilter(cat as any);
                    updateHistoryState(params, cat as any);
                  }}
                  className={`shrink-0 px-6 py-3 rounded-full text-[10px] font-black uppercase tracking-widest transition-all border-2 ${filter === cat ? 'bg-black text-white border-black shadow-xl scale-105' : 'bg-white text-gray-400 border-black/[0.05] hover:border-black/20 hover:text-black'}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3 overflow-x-auto no-scrollbar text-left pb-1">
          <div className="shrink-0 min-w-[320px] flex items-start gap-3 rounded-2xl border border-amber-500/15 bg-white/80 px-4 py-3 shadow-sm">
            <span className="shrink-0 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-[0.2em] bg-amber-400/95 text-black border border-amber-200">Verified Venue</span>
            <p className="text-[11px] md:text-xs text-gray-500 font-medium leading-relaxed">Venue-managed listing with real-time updates and full accuracy.</p>
          </div>
          <div className="shrink-0 min-w-[320px] flex items-start gap-3 rounded-2xl border border-black/5 bg-white/80 px-4 py-3 shadow-sm">
            <span className="shrink-0 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-[0.2em] bg-white text-gray-700 border border-black/10">Preview Listing</span>
            <p className="text-[11px] md:text-xs text-gray-400 font-medium leading-relaxed">Based on publicly available info. Not yet verified by the venue, so accuracy cannot be confirmed.</p>
          </div>
        </div>
      </div>

      {isLoadingDistances ? (
        <div className="py-24 text-center">
          <div className="inline-flex px-8 py-4 rounded-full bg-black text-amber-500 font-black text-xs uppercase tracking-widest animate-pulse">
            Finding nearby happy hours...
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-14">
          {filteredEvents.length > 0 ? filteredEvents.map((event) => (
            <SpecialCard
              key={event.id}
              event={event}
              distanceMiles={distanceByEventId[event.id]}
              onClick={() => onVenueSelect(event, { ...params, category: filter === 'Favorites' ? 'Saved' : filter })}
              onFavoriteToggle={(e) => toggleFavorite(e, event.id, event.venueName, event.venueId)}
              isFavorite={favorites.includes(event.id)}
              onCopyAddress={(e) => handleCopyAddress(e, event.address, event.id)}
              copiedId={copiedId}
              onGetVerified={() => onPreviewSignup(String(event.venueName || 'Venue').trim() || 'Venue')}
            />
          )) : (
            <div className="col-span-full py-20 md:py-40 text-center animate-in fade-in slide-in-from-bottom-10 duration-1000">
              <div className="max-w-3xl mx-auto space-y-10">
                <h3 className="text-4xl md:text-7xl font-black serif text-black leading-none tracking-tight">No matches yet.</h3>
                <p className="text-gray-500 font-medium text-lg md:text-2xl max-w-2xl mx-auto leading-relaxed">
                  We do not have happy hours matching that search right now. Try a wider radius, a different time, or browse all active listings while new venues are being added.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      const next = { ...params, location: '', locationLabel: '', coords: null, vibe: undefined };
                      setFilter('All');
                      setParams(next);
                      setLocationDraft('');
                      updateHistoryState(next, 'All');
                    }}
                    className="px-10 py-5 bg-black text-white rounded-full font-black text-xs md:text-sm uppercase tracking-[0.2em] shadow-2xl hover:bg-amber-500 hover:text-black hover:scale-105 transition-all active:scale-[0.98]"
                  >
                    Browse All Deals
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowNotifyModal({ venueName: params.location || 'Your City', city: params.location || 'Your City' })}
                    className="px-10 py-5 bg-white border-2 border-black/5 text-black rounded-full font-black text-xs md:text-sm uppercase tracking-[0.2em] hover:border-amber-500 hover:text-amber-600 hover:scale-105 transition-all active:scale-[0.98]"
                  >
                    Notify Me on Launch
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {showNotifyModal && (
        <SaveNotificationModal
          venueName={showNotifyModal.venueName}
          venueId={showNotifyModal.venueId}
          city={showNotifyModal.city}
          onClose={() => setShowNotifyModal(null)}
        />
      )}
    </div>
  );
};

export default ResultsPage;
